import { Container } from '@amelio/ui-library';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import EmployeesWithoutObjectivesHeader from './EmployeesWithoutObjectivesHeader';
import EmployeesWithoutObjectivesList from './EmployeesWithoutObjectivesList';
import NoObjectiveCycleState from './NoObjectiveCycleState';
import { useAddressedPageNumber } from '../hooks/useAddressedPageNumber';

import type { EmployeeWithoutObjective } from '@/types/objectives';

import { useGetCompanyInformation } from '@/api/directory';
import { useGetEmployeesWithoutObjectives } from '@/api/objectives';
import { BackLink } from '@/components/BackLink';
import { CompanyAttributeFilters } from '@/components/CompanyAttributeFilters/CompanyAttributeFilters';
import ObjectiveSearchField from '@/components/objectives/ObjectiveSearchField';
import { OBJECTIVES_PAGE_LAYOUT } from '@/components/objectives/objectiveStyles';
import { defaultFiltersState } from '@/hooks/filters/useCompanyAttributesFilters';
import { useAddressedListFilters } from '@/hooks/objectives/useAddressedListFilters';
import { useObjectivesStepBack } from '@/hooks/objectives/useObjectivesStepBack';
import { usePeriodOptions, usePeriodParam } from '@/hooks/objectives/usePeriodOptions';
import { useNavigate } from '@/hooks/utils/useNavigate';
import {
    newObjectiveForEmployeePath,
    objectivesPath,
    stepBackState,
} from '@/navigation/objectivesRoutes';
import { EMPLOYEES_WITHOUT_OBJECTIVES_PAGE_SIZE } from '@/types/objectives';
import { hasSelectedFilters, toAttributeIdParams } from '@/utils/companyAttributeFilters';
import { formatLongDate } from '@/utils/objectivesFormat';

/**
 * "Employees without an objective": a server-paginated list of the people who have no objective
 * in the selected cycle, searchable and filterable by company attribute, each tile opening the
 * objective form for that person.
 */
export default function EmployeesWithoutObjectivesPage() {
    const {
        t,
        i18n: { language },
    } = useTranslation();
    const navigate = useNavigate();
    const { stepBackOr } = useObjectivesStepBack();

    // The search, the attributes and the page live in the address, as on the objectives page: the
    // form opened for an employee steps back to this list as it was left.
    const { searchText, setSearchText, attributeFilters, setAttributeFilters } =
        useAddressedListFilters();
    const attributeSelection = attributeFilters ?? defaultFiltersState;
    const { pageNumber, setPageNumber } = useAddressedPageNumber();

    const { data: company } = useGetCompanyInformation();
    const {
        options: periodOptions,
        defaultPeriodId,
        isLoading: isLoadingPeriods,
        isError: hasPeriodsError,
    } = usePeriodOptions();
    // The same lens as the objectives overview, custom range included, so arriving from it shows
    // the window that was being looked at rather than silently falling back to a cycle.
    const { periodId, isAllPeriods, isCustomDates, customRange } = usePeriodParam(
        periodOptions,
        defaultPeriodId,
        { allowCustomDates: true },
    );
    const selectedPeriod = periodOptions.find((option) => option.id === periodId);
    // The one window this page reads: the cycle's bounds, or the reader's own two dates.
    const periodRange = isCustomDates
        ? customRange
        : { from: selectedPeriod?.from ?? '', to: selectedPeriod?.to ?? '' };

    const attributeParams = useMemo(
        () => toAttributeIdParams(attributeSelection),
        [attributeSelection],
    );
    const trimmedSearch = searchText.trim();

    // Two open bounds is a window the reader asked for under "all periods", not one still being
    // resolved: the server then counts an objective from any period as covering its holder.
    const hasWindow = isAllPeriods || !!(periodRange.from && periodRange.to);

    const { data, isLoading, error } = useGetEmployeesWithoutObjectives(
        {
            from: periodRange.from,
            to: periodRange.to,
            pageNumber,
            pageSize: EMPLOYEES_WITHOUT_OBJECTIVES_PAGE_SIZE,
            searchQuery: trimmedSearch || undefined,
            ...attributeParams,
        },
        { enabled: hasWindow },
    );

    const hasNoCycle = !isLoadingPeriods && !hasPeriodsError && periodOptions.length === 0;
    const totalCount = data?.totalCount ?? 0;
    // The count is the server's, so it is only worth showing once the server has answered for
    // the current query: a "0 employees" line while the list loads would read as a result.
    const isCountKnown = !!data && !isLoading && !isLoadingPeriods && !error;
    const lensLabel = isCustomDates ? t('objectives.period.customDates') : selectedPeriod?.label;
    const periodRangeLabel =
        periodRange.from && periodRange.to
            ? t('objectives.periodRange', {
                  start: formatLongDate(periodRange.from, language),
                  end: formatLongDate(periodRange.to, language),
              })
            : '';

    const goToObjectiveForm = (employee: EmployeeWithoutObjective) =>
        navigate({
            path: newObjectiveForEmployeePath({ employeeId: employee.id, period: periodId }),
            state: stepBackState(),
        });

    return (
        <Container
            className={OBJECTIVES_PAGE_LAYOUT.shell}
            dataTestId={'employees-without-objectives-page'}
        >
            <BackLink
                onClick={() =>
                    stepBackOr({
                        path: objectivesPath({ period: periodId, range: customRange }),
                    })
                }
                label={t('employeesWithoutObjectives.back')}
                dataTestId={'employees-without-objectives-back'}
                className={'mt-4 mb-6'}
            />

            <EmployeesWithoutObjectivesHeader />

            <div className={'obj-page-section-top flex flex-col gap-6'}>
                <div className={'flex flex-wrap items-center gap-6'}>
                    <ObjectiveSearchField
                        value={searchText}
                        onChange={setSearchText}
                        placeholder={t('employeesWithoutObjectives.searchPlaceholder')}
                        dataTestId={'employees-without-objectives-search'}
                    />

                    <CompanyAttributeFilters
                        company={company}
                        selection={attributeSelection}
                        onFiltersChange={setAttributeFilters}
                        inline
                    />
                </div>

                {isCountKnown && (isAllPeriods || (lensLabel && periodRangeLabel)) && (
                    <p
                        className={'text-obj-slate text-[14px]'}
                        data-testid={'employees-without-objectives-summary'}
                    >
                        {isAllPeriods
                            ? t('employeesWithoutObjectives.summaryAllPeriods', {
                                  count: totalCount,
                              })
                            : t('employeesWithoutObjectives.summary', {
                                  count: totalCount,
                                  cycle: lensLabel,
                                  range: periodRangeLabel,
                              })}{' '}
                        {/* Only published objectives take someone off the list: a draft does not. */}
                        {t('employeesWithoutObjectives.draftsDoNotCount')}
                    </p>
                )}
            </div>

            {/* With no period at all the lens reads every period, and the list below answers for
                that; the notice says why, and offers to create one to whoever may. */}
            {hasNoCycle && <NoObjectiveCycleState />}

            <EmployeesWithoutObjectivesList
                employees={data?.items ?? []}
                totalCount={totalCount}
                pageNumber={pageNumber}
                onPageChange={setPageNumber}
                onSelectEmployee={goToObjectiveForm}
                isLoading={isLoadingPeriods || isLoading}
                hasError={!!error || hasPeriodsError}
                hasIncompleteRange={!hasWindow && isCustomDates}
                isSearchActive={!!trimmedSearch}
                isFilterActive={hasSelectedFilters(attributeFilters)}
            />
        </Container>
    );
}
